"use client";
import React, { memo } from "react";

// --- Types ---

interface StatItem {
  id?: string | number;
  statsType?: string;
  statement: string;
  description: string;
}

interface StatsSectionProps {
  data: StatItem[]; 
}

// --- Sub-components ---

const SectionLabel = memo(({ 
  children, 
  className = "" 
}: { 
  children: React.ReactNode; 
  className?: string 
}) => (
  <p className={`leading-none not-italic relative shrink-0 text-base tracking-[0.48px] ${className}`}>
    {children}
  </p>
));

SectionLabel.displayName = "SectionLabel";

const StatCard = memo(({ label, value, statsType }: { label: string; value: string; statsType: string; }) => {
  
  // Variant: "Stat with description(Larger)" (The Default)
  let labelClasses = "text-[#727272] font-light text-md";
  let valueClasses = "text-5xl md:text-7xl lg:text-[80px] text-black font-light leading-none tracking-tighter";

  // Variant: "Statement(Larger) with description"
  if (statsType === "Statement(Larger) with description") {
    labelClasses = "text-black font-light text-[32px] md:text-[56px] leading-[1.1] normal-case mb-4";
    valueClasses = "text-[#727272] font-light text-md leading-relaxed tracking-normal";
  }

  // Variant: "Statement only"
  if (statsType === "Statement only") {
    labelClasses = "text-black font-light text-[32px] md:text-[56px] leading-[1.1] normal-case";
    valueClasses = "hidden"; // Hide the description entirely
  }

  return (
    <article className="border-l border-t border-black p-6 flex flex-col h-full transition-all hover:shadow-md hover:bg-white hover:scale-105">
      <dl className="flex flex-col h-full">
        
        {/* The Statement (Label) */}
        <dt className={statsType === "Stat with description(Larger)" ? "flex-1 mb-6" : "mb-2"}>
          <SectionLabel className={labelClasses}>
            {label}
          </SectionLabel>
        </dt>

        {/* The Description (Value) */}
        <dd className={valueClasses}>
            {value}
        </dd>
      </dl>
    </article>
  );
});

StatCard.displayName = "StatCard";

// --- Main Component ---

export default function StatsSection({ data }: StatsSectionProps) {
  if (!data || !Array.isArray(data)) {
    return null;
  }
  
  return (
    <section className="container mx-auto w-full bg-[#f6f6f6] py-12">
      <div className="">
        <div className={`grid grid-cols-1 gap-6 lg:gap-8 items-stretch ${
          data.length === 4 ? "md:grid-cols-2 lg:grid-cols-4" : "md:grid-cols-3 lg:grid-cols-3"
        }`}>
          {data.map((stat, i) => (
            <StatCard 
              key={stat?.id || i} 
              statsType={stat?.statsType || "Stat with description(Larger)"}
              label={stat?.statement} 
              value={stat?.description} 
            />
          ))}
        </div>
      </div>
    </section>
  );
}